import type { IncidentSummaryPeriod } from "../../client-messages/incidents";
import {
  fleetClientNo,
  parseTenantActivationFilters,
  type RawActivationFilters,
  type TenantActivationFilters,
} from "./activation-filters";
import { addDays } from "@visioni/a3s-dates";
import { dayIn, daysBefore, wallClockStart } from "./wall-clock";

/** How far back the incident views reach when the link names no start day. */
export const INCIDENT_DEFAULT_DAYS = 30;

const PERIODS: readonly IncidentSummaryPeriod[] = ["day", "week", "month"];

/**
 * Which incidents a view lists and sums, and what it sums them into. Always
 * bounded below, because the fleet list and every summary need a start.
 */
export interface IncidentFilters extends TenantActivationFilters {
  readonly since: string;
}

/** The fleet list's filters: no site or camera, but the start of a client number, as the fleet activations take it. */
export interface FleetIncidentFilters extends Omit<IncidentFilters, "site" | "camera"> {
  readonly clientNo?: string;
}

export type ParsedIncidentFilters =
  | {
      readonly ok: true;
      readonly filters: IncidentFilters & Pick<FleetIncidentFilters, "clientNo">;
      readonly period: IncidentSummaryPeriod;
    }
  | { readonly ok: false; readonly problem: string };

/**
 * Reads incident filters from raw URL params: the activation lists' filters,
 * against when an incident started, plus the `period` the totals are summed into.
 *
 * A link naming no start shows the {@link INCIDENT_DEFAULT_DAYS} days up to its
 * end, or up to `today` in `tz`: a day rather than an instant, so the query and
 * its cursor stay the same across renders. The fleet view passes no site or
 * camera, and may pass the start of a client number.
 */
export function parseIncidentFilters(
  raw: RawActivationFilters & { readonly period?: string | undefined; readonly clientNo?: string | undefined },
  today: Date,
): ParsedIncidentFilters {
  const period = raw.period === undefined || raw.period === "" ? "day" : raw.period;
  if (!isPeriod(period)) {
    return { ok: false, problem: `The period "${period}" in this link is not day, week or month.` };
  }

  const parsed = parseTenantActivationFilters(raw);
  if (!parsed.ok) return parsed;

  const to = raw.to === "" ? undefined : raw.to;
  const from = daysBefore(to ?? dayIn(today, raw.tz), INCIDENT_DEFAULT_DAYS - 1);
  const since = parsed.filters.since ?? (from === undefined ? undefined : wallClockStart(from, raw.tz));
  if (since === undefined) return { ok: false, problem: "The start of this range is not valid." };

  const clientNo = fleetClientNo(raw.clientNo);
  return { ok: true, filters: { ...parsed.filters, since, ...(clientNo !== undefined && { clientNo }) }, period };
}

/** The first and last day of one row of the totals: its day, week or month (`label` as the summary gives it). */
export function incidentPeriodDays(row: { readonly period: IncidentSummaryPeriod; readonly label: string }): {
  readonly first: string;
  readonly last: string;
} {
  const first = row.period === "month" ? `${row.label}-01` : row.label;
  const last =
    row.period === "day" ? first
    : row.period === "week" ? addDays(first, 6)
    : addDays(`${addDays(`${row.label}-28`, 4).slice(0, 7)}-01`, -1);
  return { first, last };
}

/**
 * The `from` / `to` that list one row of the totals: its day, week or month
 * (`label` as the summary gives it), cut to the range the totals read, so the
 * list holds what the row counted — times included where the range has them.
 *
 * Read the totals' `raw` as {@link parseIncidentFilters} does: with no start,
 * the default days up to its end, or up to `today` in `tz`.
 */
export function incidentPeriodRange(
  raw: Pick<RawActivationFilters, "from" | "to" | "tz">,
  row: { readonly period: IncidentSummaryPeriod; readonly label: string },
  today: Date,
): { readonly from: string; readonly to: string } {
  const { first, last } = incidentPeriodDays(row);
  const from = raw.from ?? daysBefore(raw.to ?? dayIn(today, raw.tz), INCIDENT_DEFAULT_DAYS - 1);
  return {
    from: from !== undefined && from.slice(0, 10) >= first ? from : first,
    to: raw.to !== undefined && raw.to.slice(0, 10) <= last ? raw.to : last,
  };
}

const isPeriod = (value: string): value is IncidentSummaryPeriod => PERIODS.some((period) => period === value);

/** Identity of an incident list, for anything that must start over when it changes. */
export function incidentFiltersKey(filters: FleetIncidentFilters & Pick<IncidentFilters, "site" | "camera">): string {
  return [filters.site, filters.camera, filters.since, filters.until, filters.order, filters.clientNo]
    .map((v) => v ?? "")
    .join("|");
}
